import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { PaymentEvent } from 'tio-data/types/tuition-assistance';

/**
 * YYYY-MM-DD, `days` from today's local calendar date. Every other date in
 * this file is a fixed 2026 literal — fine, since nothing branches on them
 * relative to the real current date. `COURSES_END_DATE` is the one
 * exception: deriveAttention's evidence-urgency tiers (Default/Warning/
 * Error) compare it against `new Date()`, so a fixed literal would silently
 * drift into a different tier as real time passes, breaking both the named
 * gallery scenario and its regression test (caught in review on #9915).
 */
function daysFromToday(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Plain-object fixtures for the TAS course-management ("manage") screen, one per
 * derivation branch in `deriveAttention`. Used by the dev-only manage gallery
 * route and by the ManagePage integration test, so both stay in sync.
 *
 * These are NOT live WarpDrive records — they expose only the getters/relations
 * ManagePage + deriveAttention + NeedsYourAttention read, then cast to the model
 * type. Money is in cents (the screen converts to dollars).
 */
export interface ManageFixture {
  key: string;
  label: string;
  application: TasApplicationModel;
}

interface FixtureOverrides {
  id?: string;
  displayName?: string;
  state?: string;
  reserved?: number;
  used?: number;
  isEvidenceRequired?: boolean;
  isMissingRequiredEvidence?: boolean;
  isApproved?: boolean;
  approvedTotal?: number;
  paidTotal?: number;
  shouldDisplayApprovedTotal?: boolean;
  shouldDisplayPaymentDetails?: boolean;
  paymentsHistory?: PaymentEvent[];
  receiptUploaded?: boolean;
  withComment?: boolean;
  isDirectPayProgram?: boolean;
  /** The application's own payment election (`paymentMethod` /
   * `PAYMENT_TYPE_FOR_DIRECT_PAY`) — what the disbursement rail's CC$ card
   * gates on (#10392). Defaults to following `isDirectPayProgram`, since every
   * pre-existing CC$ fixture models an application that genuinely elected the
   * card; pass `false` with `isDirectPayProgram: true` for the
   * reimbursement-elected-on-a-CC$-program shape. */
  courseCashElected?: boolean;
  typeClassification?: string;
  /** Disbursement rail — program payment timing (Pre-Paid vs Reimbursement). */
  isPrepaidProgram?: boolean;
  /** Disbursement rail — reimbursement money assistances (drive the per-payment
   * rows and the manner override of `isPrepaidProgram`); appended to
   * `tasAssistances`, which otherwise stays empty by default (see its comment). */
  reimbursementAssistances?: {
    amount: number;
    state: 'TAS.AssistanceState.SETTLED' | 'TAS.AssistanceState.PAYABLE';
    settledDate?: string;
  }[];
  /** Disbursement rail — pre-approval CC$-eligible (tuition) subtotal, independent of `reserved`. */
  requestedCourseCashSubtotal?: number;
  /** Disbursement rail — pre-approval non-CC$ subtotal (books/labs/etc, or the full total on a non-CC$ program). */
  requestedNonCourseCashSubtotal?: number;
  /** Disbursement rail — marks the fixture's single pcsCard (when `reserved > 0`) as deactivated. */
  pcsCardDeactivated?: boolean;
  /** Disbursement rail — override the flat aggregate requested total (default 139600). */
  requestedTotal?: number;
  /** Disbursement rail — the live CC$ reserve's rolling funds-expiry date (raw YYYY-MM-DD). */
  courseCashExpiresAt?: string | null;
  /** Adds a live AUTHORIZED (swiped, not yet captured) CC$ assistance — drives
   * the pending-capture "Processing" row. Like courseCashExpiresAt,
   * opt-in so tasAssistances stays empty by default (see the comment there). */
  pendingCaptureSwipe?: boolean;
  /** Adds a live CAPTURED CC$ assistance with a PENDING receipt approvable —
   * drives the uploaded-receipt status row + its View entry point (#10291). */
  uploadedReceiptSwipe?: boolean;
  /** Raw `COURSES_END_DATE` (YYYY-MM-DD) — drives submit-evidence's due-date/urgency. */
  courseEndDate?: string;
  /** Raw `POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME` program-template field (days). */
  evidenceMaxLagDays?: number;
  /** Cents the employee has been asked to repay — drives the in-repayment anchor. */
  refundRequested?: number;
  /** Cents already repaid, against `refundRequested`. */
  refundPaidBack?: number;
  /** Template `attachmentsForCourseApprovalVisible` (mirrored as a plain field) —
   * turns the course-approval documents surface on (#10292). */
  courseApprovalDocumentsVisible?: boolean;
  /** Template `requireAttachmentsForCourseApproval`, via the model's
   * `isPreApprovalAttachmentsRequired` (mirrored plain) — drives the card's
   * required-vs-optional wording. */
  courseApprovalDocumentsRequired?: boolean;
  /** Drops the default COURSE_APPROVAL_DOCUMENTS attachment (transcript.pdf)
   * from tasAssets — and therefore from the mirrored applicationApprovalAssets
   * slice — for the "required document still missing" shape. */
  withoutCourseApprovalDocument?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- plain fixture data mirroring TransitionLogSignature[]; typing it exactly would require importing tio-data's type into a fixture module that otherwise stays structural.
  transitionLog?: any[];
}

const LOREM =
  'Thanks for submitting — your reviewer will reach out if anything else is needed for this request.';

/** Author of the `withComment` fixture comment. Exported so a test can stand in
 * as this person and assert the unread badge suppresses its own comments. */
export const FIXTURE_APPROVER_PERSON_ID = 'fixture-approver-person-id';

/** The `withComment` fixture comment's own id. Read state is tracked by comment id
 * (see utils/tas/comments), so a comment without one could never be marked seen. */
export const FIXTURE_COMMENT_ID = 'fixture-comment-id';

// ApplicationHistoryTimeline drops CREATED entries as noise (see its own
// timelineEvents filter) — kept here anyway since it's a faithful history
// record; the Timeline drawer renders the other two.
const HISTORY_ENTRIES = [
  {
    date: '2026-06-01T00:00:00Z',
    from: 'CREATED',
    to: 'CREATED',
    logType: 'state_transition',
    stateTransitionType: 'CREATED',
  },
  {
    date: '2026-06-01T12:00:00Z',
    from: 'CREATED',
    to: 'PENDING_COURSES_APPROVAL',
    logType: 'state_transition',
    stateTransitionType: 'PENDING_COURSES_APPROVAL',
  },
  {
    date: '2026-06-05T00:00:00Z',
    from: 'PENDING_COURSES_APPROVAL',
    to: 'COURSES_APPROVED',
    logType: 'state_transition',
    stateTransitionType: 'COURSES_APPROVED',
    approverName: 'Karen Advocate',
    approverComment: 'Approved — thanks for the detailed info.',
  },
];

// Feeds the Attachments drawer (application.tasAssets, aggregated by
// ManagePage#allAttachments). `extension` is a getter on the real model
// (derived from filename) — mirrored here as a plain field, same reason as
// fullHistory above.
const TAS_ASSETS = [
  {
    id: 'fixture-asset-transcript',
    filename: 'transcript.pdf',
    extension: 'pdf',
    classification: 'COURSE_APPROVAL_DOCUMENTS',
    createdAt: '2026-06-03T00:00:00Z',
    attachmentDeletedAt: null,
    signedUrl: '#',
    size: 245000,
  },
  {
    id: 'fixture-asset-grade-report',
    filename: 'grade-report.png',
    extension: 'png',
    classification: 'COMPLETION_DOCUMENTS',
    createdAt: '2026-06-08T00:00:00Z',
    attachmentDeletedAt: null,
    signedUrl: '#',
    size: 88000,
  },
];

// Attached at the program-instance level (e.g. a program-wide agreement),
// not the application — exercises ManagePage#allAttachments' aggregation of
// `application.tasProgramInstance.tasAssets`, which manage-route.ts must
// include via `tas-program-instance.tas-assets.attachments` for this to be
// populated on a real page load.
const PROGRAM_INSTANCE_TAS_ASSETS = [
  {
    id: 'fixture-asset-program-agreement',
    filename: 'program-agreement.pdf',
    extension: 'pdf',
    classification: 'PROGRAM_AGREEMENT_DOCUMENTS',
    createdAt: '2026-05-20T00:00:00Z',
    attachmentDeletedAt: null,
    signedUrl: '#',
    size: 62000,
  },
];

// A live AUTHORIZED swipe (plain mirror of the TasAssistance ext getters the
// transaction adapter reads — isAuthorized/authorizedDate are derived from
// state/transitionLog on the real model, mirrored as fields here like
// courseCashAvailableBalance below).
const AUTHORIZED_SWIPE_ASSISTANCE = {
  id: 'fixture-authorized-swipe-id',
  isCourseCash: true,
  isAuthorized: true,
  isCaptured: false,
  authorizedDate: new Date('2026-07-02T12:00:00Z'),
  amount: 25000,
  merchantName: 'Healthy Hair Bookstore',
  attachments: [],
  tasAssets: [],
  courseCashApprovables: [],
  fields: {},
};

// A live CAPTURED swipe whose receipt is uploaded and awaiting admin review:
// the PENDING approvable + its asset/attachment resolve to receiptStatus
// 'uploaded' through the real adapter (determineReceiptStatus). `belongsTo` is
// the same plain stub shape transaction-adapter-receipts-test.ts uses — the
// adapter reads the raw linkage rather than traversing the polymorphic
// relationship (digital-assets vs tas-assets STI mismatch).
const UPLOADED_RECEIPT_ASSISTANCE = {
  id: 'fixture-uploaded-swipe-id',
  isCourseCash: true,
  isCaptured: true,
  capturedDate: new Date('2026-06-20T12:00:00Z'),
  receiptDueDate: new Date('2026-07-11T12:00:00Z'),
  amount: 35000,
  grossAmount: null,
  merchantName: 'Healthy Hair Academy',
  digests: null,
  attachments: [],
  fields: {},
  tasAssets: [
    {
      id: 'fixture-receipt-asset',
      attachments: [
        {
          id: 'fixture-receipt-attachment',
          filename: 'bursar-receipt.pdf',
          contentType: 'application/pdf',
          signedUrl: '#',
        },
      ],
    },
  ],
  courseCashApprovables: [
    {
      id: 'fixture-receipt-approvable',
      state: 'ApprovableState.PENDING',
      isAbandoned: false,
      isApproved: false,
      isPreparing: false,
      isRejected: false,
      isCourseCashUsageReview: true,
      createdAt: '2026-06-21T00:00:00Z',
      belongsTo: () => ({
        id: () => 'fixture-receipt-asset',
        identifier: { type: 'digital-assets', id: 'fixture-receipt-asset' },
      }),
    },
  ],
};

function buildApplication(overrides: FixtureOverrides = {}): TasApplicationModel {
  const reserved = overrides.reserved ?? 0;
  const used = overrides.used ?? 0;
  const tasAssets = overrides.withoutCourseApprovalDocument
    ? [
        ...TAS_ASSETS.filter((asset) => asset.classification !== 'COURSE_APPROVAL_DOCUMENTS'),
        // Decoy: the model's applicationApprovalAssets getter also admits
        // lifecycle documents, so the "required document still missing" shape
        // carries one — the course-documents task must stay PENDING despite it
        // (the classification filter in courseApprovalDocumentAssets is what
        // this guards).
        {
          id: 'fixture-lifecycle-asset-id',
          filename: 'w9.pdf',
          extension: 'pdf',
          classification: 'APPLICATION_LIFECYCLE_DOCUMENTS',
          createdAt: '2026-06-03',
          attachmentDeletedAt: null,
          signedUrl: 'https://example.test/w9.pdf',
          size: 12000,
        },
      ]
    : TAS_ASSETS;
  return {
    id: overrides.id ?? 'fixture-app-id',
    displayName: overrides.displayName ?? 'Summer Term 2026',
    state: overrides.state ?? 'TAS.ApplicationState.ATTEND',
    transitionLog: overrides.transitionLog ?? [],
    amounts: {
      course_cash: { reserved, used },
      // Repayment is tracked independently of application state — a FULFILLED
      // request can still owe money back.
      refund_requested: overrides.refundRequested ?? 0,
      refund_paid_back: overrides.refundPaidBack ?? 0,
      refund_forgiven: 0,
      refund_cancelled: 0,
    },
    // Mirrors the real model getter (outstanding refund balance > 0), which is
    // what the legacy show page badges "In Repayment" from.
    inRepayment: (overrides.refundRequested ?? 0) - (overrides.refundPaidBack ?? 0) > 0,
    // Mirrors the real model getter: `reserved` IS the remaining balance, so the
    // available balance is `reserved` — NOT `reserved - used`, which would
    // double-subtract the spend. Fixtures that model spending must therefore
    // reduce `reserved` rather than leaving it at the original allocation.
    courseCashAvailableBalance: Math.max(0, reserved),
    isEvidenceRequired: overrides.isEvidenceRequired ?? false,
    isMissingRequiredEvidence: overrides.isMissingRequiredEvidence ?? false,
    // A getter on the real model (evidence assets per course / at the application
    // level), mirrored as a plain field here for the same reason as
    // courseCashAvailableBalance and fullHistory. Without it every fixture read
    // `undefined`, so ManagePage#canSubmitEvidence could never be true and the
    // disabled-submit assertions passed for the wrong reason. Derived from
    // isMissingRequiredEvidence so a fixture cannot claim both at once.
    hasRequiredEvidenceAttachments: !(overrides.isMissingRequiredEvidence ?? false),
    isApproved: overrides.isApproved ?? false,
    approvedTotal: overrides.approvedTotal ?? 134600,
    // Mirrors real data: the backend syncs PRE into FINAL on save, so both
    // fields carry the amount; the manage rail decides what to show by state.
    preApprovedTotal: overrides.approvedTotal ?? 134600,
    finalApprovedTotal: overrides.approvedTotal ?? 134600,
    paidTotal: overrides.paidTotal ?? 0,
    requestedTotal: overrides.requestedTotal ?? 139600,
    isPrepaidProgram: overrides.isPrepaidProgram ?? false,
    // Defaults mirror the two activeCourses below (100000 tuition + 39600
    // books/labs) so a CC$ fixture's card gets a plausible tuition-only
    // subtotal without every scenario having to override both explicitly.
    requestedCourseCashSubtotal:
      overrides.requestedCourseCashSubtotal ?? (reserved > 0 ? 100000 : 0),
    requestedNonCourseCashSubtotal:
      overrides.requestedNonCourseCashSubtotal ?? (reserved > 0 ? 39600 : 139600),
    shouldDisplayApprovedTotal: overrides.shouldDisplayApprovedTotal ?? true,
    shouldDisplayPaymentDetails: overrides.shouldDisplayPaymentDetails ?? false,
    paymentsHistory: overrides.paymentsHistory ?? [],
    isCreditBased: false,
    totalCredits: 0,
    receiptUploaded: overrides.receiptUploaded ?? false,
    applicationSubmittedDate: '2026-06-01',
    coursesBeginDate: '2026-06-15',
    fields: { COURSES_END_DATE: { values: [overrides.courseEndDate ?? daysFromToday(90)] } },
    activeCourses: [
      { displayName: 'Intro to Nursing', courseTuition: 100000 },
      { displayName: 'Pharmacology I', courseTuition: 39600 },
    ],
    comments: overrides.withComment
      ? [
          {
            // Both ids matter to the unread-comment badge (see utils/tas/comments):
            // read state is tracked by comment id, and authorship is attributed by
            // person id. Without them neither path can be exercised.
            id: FIXTURE_COMMENT_ID,
            comment: LOREM,
            tags: ['employee'],
            person: {
              id: FIXTURE_APPROVER_PERSON_ID,
              firstName: 'Karen',
              lastName: 'Advocate',
            },
            createdAt: '2026-06-10',
            updatedAt: '2026-06-10',
          },
        ]
      : [],
    // Opt-in only (courseCashExpiresAt/swipe overrides set) — NOT gated on
    // reserved/used like pcsCards below. Other tests rely on tasAssistances
    // being empty by default to exercise the "no live assistance data, fall
    // back to the placeholder receiptUploaded flag" path (see attentionModel's
    // hasLiveCourseCashData check) — populating it broadly would silently
    // flip that behavior for every existing CC$ fixture.
    tasAssistances: [
      ...(overrides.courseCashExpiresAt != null
        ? [
            {
              id: 'fixture-course-cash-assistance-id',
              isCourseCash: true,
              courseCashExpiresAt: overrides.courseCashExpiresAt,
            },
          ]
        : []),
      ...(overrides.pendingCaptureSwipe ? [AUTHORIZED_SWIPE_ASSISTANCE] : []),
      ...(overrides.uploadedReceiptSwipe ? [UPLOADED_RECEIPT_ASSISTANCE] : []),
      // Reimbursement money assistances — mirror the real-model getters
      // (isSettled/settledDate) as plain fields, same as everywhere above.
      ...(overrides.reimbursementAssistances ?? []).map((assistance, index) => ({
        id: `fixture-reimbursement-assistance-${index}`,
        manner: 'TAS.AssistanceManner.REIMBURSEMENT',
        state: assistance.state,
        amount: assistance.amount,
        isSettled: assistance.state === 'TAS.AssistanceState.SETTLED',
        settledDate: assistance.settledDate ?? null,
      })),
    ],
    // Mirrors the real model getter (any REIMBURSEMENT-manner assistance).
    hasReimbursementAssistance: (overrides.reimbursementAssistances ?? []).length > 0,
    // Mirrors the real model getter (PAYMENT_TYPE_FOR_DIRECT_PAY on the
    // application's own fields, REIMBURSEMENT default) — the rail's CC$ gate
    // (#10392). Follows the template flag unless a scenario decouples them.
    paymentMethod:
      (overrides.courseCashElected ?? overrides.isDirectPayProgram ?? false)
        ? 'TAS.AssistanceManner.COURSE_CASH'
        : 'TAS.AssistanceManner.REIMBURSEMENT',
    // A fully-spent-and-deactivated card correctly has `reserved` at (or near) 0
    // (see disbursement.ts's module doc — reserved is the current remaining
    // balance, not a fixed original allocation), so the card's presence can't
    // gate on `reserved > 0` alone once spending is modeled realistically.
    pcsCards:
      reserved > 0 || used > 0
        ? [
            {
              id: 'fixture-pcs-card-id',
              state: overrides.pcsCardDeactivated
                ? 'PCS.CardState.INACTIVATED'
                : 'PCS.CardState.ACTIVATED',
              isActive: !overrides.pcsCardDeactivated,
              isDeactivated: !!overrides.pcsCardDeactivated,
              providerData: { last4: '8882' },
              pcsCardHolder: {
                legalName: 'Fixture Employee',
                providerData: { billing: { address: { postal_code: '96324' } } },
              },
              // No providerId here or on pcsCompany → Stripe Issuing setup is
              // skipped entirely (same pattern as stripe-card-display-test.gts's
              // fixtures) — the Card Details tab renders its masked fallback in
              // tests/dev gallery instead of attempting a real Stripe.js load.
              // providerData still carries the company address the tab composes
              // its billing-address row from.
              pcsCompany: {
                providerData: {
                  company: {
                    address: {
                      line1: '1 Fixture Way',
                      city: 'Fixture City',
                      state: 'CA',
                      postal_code: '96324',
                    },
                  },
                },
              },
            },
          ]
        : [],
    // Feeds the Timeline drawer's ApplicationHistoryTimeline. `fullHistory` is a
    // getter on the real model (application_history ++ paymentsHistory) — fixtures
    // are plain objects, not real WarpDrive records, so it's mirrored here as a
    // plain field, same as courseCashAvailableBalance/preApprovedTotal above.
    // Not overridable per-scenario — a plausible, fixed submission/approval
    // sequence is enough to prove the drawer renders real content everywhere.
    history: { application_history: HISTORY_ENTRIES },
    fullHistory: [...HISTORY_ENTRIES, ...(overrides.paymentsHistory ?? [])],
    tasAssets,
    // Mirrors the real model getter (the live COURSE_APPROVAL_DOCUMENTS /
    // APPLICATION_LIFECYCLE_DOCUMENTS slice of tasAssets) — kept derived from
    // the same tasAssets array built above so the two plain fields cannot
    // contradict each other, the same way hasRequiredEvidenceAttachments is
    // derived from isMissingRequiredEvidence.
    applicationApprovalAssets: tasAssets.filter(
      (asset) =>
        !asset.attachmentDeletedAt &&
        (asset.classification === 'COURSE_APPROVAL_DOCUMENTS' ||
          asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS')
    ),
    // Mirrors the real model getter (template requireAttachmentsForCourseApproval).
    isPreApprovalAttachmentsRequired: overrides.courseApprovalDocumentsRequired ?? false,
    tasProgramInstance: {
      // Required for ActionToolbar#canWithdraw: Type 4.2/4.3 applications
      // (the fixture default) can only withdraw while the parent instance is
      // still PRIMED — same gate the legacy show.gts page uses.
      state: 'TAS.ProgramInstanceState.PRIMED',
      fields: { SCHOOL_INSTITUTION_NAME: { values: ['Healthy Hair Academy'] } },
      tasAssets: PROGRAM_INSTANCE_TAS_ASSETS,
      tasProgramTemplate: {
        programName: "Karen's Advocate Health Program",
        isDirectPayProgram: overrides.isDirectPayProgram ?? false,
        typeClassification: overrides.typeClassification ?? 'TAS.ProgramType.4.3',
        // Province `application_approve_evidence_workflow` — the evidence-time
        // terms. Every seeded program template carries one, so the fixtures
        // should too: without it the evidence card's terms never render in the
        // gallery or in tests.
        applicationApproveEvidenceConditions: [
          {
            id: 'fixture-condition-evidence',
            prompt:
              'Please read and accept before submitting your course evidence for reimbursement',
            hasPdf: false,
          },
        ],
        // tio-api seed default is 45 days (TasApplication#post_courses_end_
        // auto_rejection_date) — admin-configurable per program template.
        fields: {
          POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME: {
            values: [overrides.evidenceMaxLagDays ?? 45],
          },
        },
        // Template-extension methods the categorized-upload gate reads
        // (tas-program-template.ext.ts). The fixture template configures no
        // document categories, so surfaces fall back to the legacy uploader —
        // matching the pre-categorized behavior these tests assert.
        documentEntriesFor: () => [],
        requiredDocumentTypesFor: () => [],
        // Template getter mirrored plain (COURSE_APPROVAL_DOCUMENTS_STRING's
        // visible flag) — opt-in, so no pre-existing fixture grows the
        // course-approval documents task (#10292).
        attachmentsForCourseApprovalVisible: overrides.courseApprovalDocumentsVisible ?? false,
      },
    },
  } as unknown as TasApplicationModel;
}

export const MANAGE_FIXTURES: ManageFixture[] = [
  {
    key: 'rejected',
    label: 'Changes requested (RejectionDetail banner)',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_REJECTED',
      shouldDisplayApprovedTotal: false,
      typeClassification: 'TAS.ProgramType.3',
      transitionLog: [
        {
          event: 'reject_courses',
          from: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          to: 'TAS.ApplicationState.COURSES_REJECTED',
          timestamp: '2026-06-12T00:00:00Z',
          reason: {
            by: { name: 'Karen Advocate' },
            note: 'Please add the missing course description before resubmitting.',
          },
        },
      ],
    }),
  },
  {
    key: 'evidence-request-change',
    // Distinct from 'rejected': the approver's "Request Change" action on an
    // evidence review fires `decline_evidence` (not `reject_evidence`),
    // landing in EVIDENCE_NOT_APPROVED — a regression case for the bug where
    // RejectionDetail's event lookup only recognized reject_*/not_approve_*
    // (the latter was never a real event name), so this exact flow rendered
    // with a blank reviewer note.
    label: 'Evidence changes requested via "Request Change" (RejectionDetail banner)',
    application: buildApplication({
      state: 'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
      isApproved: true,
      shouldDisplayApprovedTotal: false,
      typeClassification: 'TAS.ProgramType.4.3',
      transitionLog: [
        {
          event: 'decline_evidence',
          from: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
          to: 'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
          timestamp: '2026-07-20T00:00:00Z',
          reason: {
            by: { name: 'Karen Advocate' },
            note: 'The uploaded transcript is missing a final grade — please resubmit with the completed transcript.',
          },
        },
      ],
    }),
  },
  {
    key: 'evidence-request-change-revising',
    // The 8/7 QA shape behind #10394: the request-change was pulled back out
    // of EVIDENCE_NOT_APPROVED (`attend`) and the employee is mid-revision.
    // The legacy page keeps its instructions banner up through this window;
    // the DecisionNotice must too — reframed as a change request (warning
    // tone, "Requested by …", no resubmit CTA on an already-reopened
    // application).
    label: 'Request change being revised (instructions persist, no CTA)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      shouldDisplayApprovedTotal: false,
      typeClassification: 'TAS.ProgramType.4.3',
      transitionLog: [
        {
          event: 'decline_evidence',
          from: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
          to: 'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
          timestamp: '2026-07-20T00:00:00Z',
          reason: {
            by: { name: 'Karen Advocate' },
            note: 'It is missing all the required documentation — please attach the receipts before resubmitting.',
          },
        },
        {
          event: 'attend',
          from: 'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
          to: 'TAS.ApplicationState.ATTEND',
          timestamp: '2026-07-21T00:00:00Z',
          reason: {},
        },
      ],
    }),
  },
  {
    key: 'submitted',
    label: 'Submitted / under review (anchor)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'submitted-evidence-required',
    // Same under-review state as 'submitted', but on a template that requires
    // completion evidence. Evidence is trivially "missing" here — the courses
    // have not even been approved yet — which used to surface a "Submit your
    // Evidence Items" task directly beneath the "under review" anchor, telling
    // the employee to do work they cannot do. Nothing may be pending here.
    label: 'Submitted / under review, evidence-required template (no pending tasks)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
    }),
  },
  {
    key: 'evidence-submitted',
    label: 'Evidence submitted / under review (anchor)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      // Mirrors the real model: isApproved stays true from COURSES_APPROVED
      // through PENDING_EVIDENCE_APPROVAL, so this must NOT surface the
      // "approved" anchor — the evidence-submitted/under-review anchor takes priority.
      isApproved: true,
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'evidence-submitted-with-active-task',
    label:
      'Evidence submitted, CourseCash still unspent (evidence-submitted anchor + task together)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      // Regression fixture: an unspent CourseCash card is still an active task
      // while evidence review is pending — the "evidence-submitted" anchor must
      // show alongside it, not get crowded out by the non-zero pendingCount.
      isApproved: true,
      shouldDisplayApprovedTotal: false,
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: false,
    }),
  },
  {
    key: 'evidence-submitted-course-cash-spent',
    label: 'Evidence submitted, CourseCa$h already spent (rail must still say in review)',
    // The shape PM reported: the rail lists a COMPLETED CourseCa$h row, which used
    // to suppress the review status entirely — so the panel said "your items have
    // been submitted" while the rail said nothing about review. Distinct from
    // `evidence-submitted-with-active-task`, where the card is unspent and the row
    // is therefore pending rather than completed.
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      isApproved: true,
      shouldDisplayApprovedTotal: false,
      reserved: 0,
      used: 101800,
      // Receipt already in, so the completed card is the only rail row.
      receiptUploaded: true,
      isDirectPayProgram: true,
      requestedCourseCashSubtotal: 100000,
      requestedNonCourseCashSubtotal: 39600,
    }),
  },
  {
    key: 'all-caught-up',
    label: 'All caught up (empty state)',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      // Mirrors the real model: isApproved stays true through FULFILLED, so
      // this must NOT surface the "approved" anchor — all-caught-up wins.
      isApproved: true,
      withComment: true,
    }),
  },
  {
    key: 'evidence-approved',
    // The QA 7/27 shape behind #10285: final (evidence) approval done — via
    // the normal flow or an audit-queue correction — but the payout hasn't
    // closed the request yet. Used to fall through to the "application in
    // review" rail filler.
    label: 'Evidence approved (final approval done, not yet fulfilled)',
    application: buildApplication({
      state: 'TAS.ApplicationState.EVIDENCE_APPROVED',
      isApproved: true,
    }),
  },
  {
    key: 'fulfilled-course-cash-spent',
    label: 'Fulfilled + CourseCa$h spent (rail keeps the completed card row)',
    // The shape `all-caught-up` above does NOT cover: closed AND holding a
    // spent card, so the rail lists the completed use-course-cash task while
    // the application itself is complete. Amounts mirror a real staging record
    // ($5.00 approved, $4.50 spent, $0.50 left).
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      reserved: 50,
      used: 450,
      // Receipt already in, so the only rail row is the completed card itself.
      receiptUploaded: true,
      isDirectPayProgram: true,
    }),
  },
  {
    key: 'in-repayment',
    // Mirrors a real record: FULFILLED and paid out, but the employee was later
    // asked to repay $7.43 and none of it is settled. Application state and
    // payment state are independent, so this must NOT read as all-caught-up.
    label: 'In repayment (fulfilled, outstanding balance)',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      refundRequested: 743,
      withComment: true,
    }),
  },
  {
    key: 'panel-hidden',
    // COURSES_NOT_APPROVED with nothing required: the DecisionNotice (a
    // separate component) carries the messaging, so the panel itself has
    // nothing to surface. ATTEND can no longer model this — it always carries
    // the submit-evidence card (#10395, `attend-no-evidence-required` below).
    label: 'Panel suppressed (returned to employee, nothing to surface)',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_NOT_APPROVED',
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'attend-no-evidence-required',
    // The 8/7 QA shape behind #10395: preapproval done, template requires
    // neither completion documents nor grades. This used to render ONLY the
    // approved anchor — read as "already approved", with no way to submit for
    // final approval. The submit-evidence card (in-progress, submit affordance
    // in its footer) must render regardless of upload requirements.
    label: 'ATTEND, no evidence/grades required (submit affordance must remain)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      approvedTotal: 600,
      requestedTotal: 600,
      requestedNonCourseCashSubtotal: 600,
      isEvidenceRequired: false,
      isMissingRequiredEvidence: false,
      courseEndDate: daysFromToday(30),
    }),
  },
  {
    key: 'abandoned',
    // Deliberately carries stale actionable data (unspent CourseCash card,
    // missing evidence) as if the request was abandoned mid-flow — proves
    // deriveAttention strips those rather than rendering them alongside the
    // abandoned anchor (see attention-tasks-test.ts's regression test).
    label: 'Withdrawn / abandoned (anchor, with stale leftover tasks)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ABANDONED',
      shouldDisplayApprovedTotal: false,
      isApproved: true,
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
    }),
  },
  {
    key: 'evidence-only',
    label: 'Submit evidence — Default (no CourseCash, course not yet ended)',
    application: buildApplication({
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      withComment: true,
    }),
  },
  {
    key: 'evidence-warning',
    // Course ended 30 days ago; due date (course end + 45-day lag) is still
    // 15 days out — past the Default cutoff but nowhere near the 7-day
    // Error window. Relative to today (not a fixed literal) so this stays
    // Warning forever instead of drifting into Error as real time passes.
    label: 'Submit evidence — Warning (course ended, due date not yet urgent)',
    application: buildApplication({
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      courseEndDate: daysFromToday(-30),
      evidenceMaxLagDays: 45,
    }),
  },
  {
    key: 'evidence-error',
    // A shorter 14-day lag (some program templates configure less than the
    // 45-day seed default) puts the due date well in the past — relative to
    // today so this stays Error forever.
    label: 'Submit evidence — Error (due date imminent/passed, shorter 14-day lag)',
    application: buildApplication({
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      courseEndDate: daysFromToday(-60),
      evidenceMaxLagDays: 14,
    }),
  },
  {
    key: 'course-documents-required',
    // The QA-reported shape (#10292): a template that requires attachments for
    // course approval, with nothing uploaded yet, sitting in review. The legacy
    // show page renders its asterisked attachments section here; the manage
    // screen must surface the pending upload-course-documents task beside the
    // submitted anchor.
    label: 'Course-approval documents — required, none uploaded (pending task)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
      courseApprovalDocumentsVisible: true,
      courseApprovalDocumentsRequired: true,
      withoutCourseApprovalDocument: true,
    }),
  },
  {
    key: 'course-documents-uploaded',
    // Same state with the default transcript.pdf (COURSE_APPROVAL_DOCUMENTS)
    // still in tasAssets: the task completes, leaves the pending cards, and
    // stays reachable as the collapsed drawer listing the uploaded document.
    label: 'Course-approval documents — one uploaded (completed, drawer card)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
      courseApprovalDocumentsVisible: true,
      courseApprovalDocumentsRequired: true,
    }),
  },
  {
    key: 'course-cash-available',
    label: 'CourseCash available + evidence',
    application: buildApplication({
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      isDirectPayProgram: true,
    }),
  },
  {
    key: 'approved',
    label: 'Approved (anchor) + CourseCash + evidence',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_APPROVED',
      isApproved: true,
      reserved: 101800,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      isDirectPayProgram: true,
      withComment: true,
    }),
  },
  {
    key: 'course-cash-used',
    label: 'CourseCash used → receipt + evidence (used anchor)',
    application: buildApplication({
      // Fully spent, so the remaining balance (`reserved`) is 0 — the $1,018.00
      // original allocation is used + reserved. The subtotals are pinned because
      // their defaults key off `reserved > 0`, which no longer holds here.
      reserved: 0,
      used: 101800,
      requestedCourseCashSubtotal: 100000,
      requestedNonCourseCashSubtotal: 39600,
      paidTotal: 101800,
      // Mirrors the real model: isApproved stays true through ATTEND — must not
      // leak the "approved" anchor alongside course-cash-used (Figma: used only).
      isApproved: true,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      shouldDisplayPaymentDetails: true,
      isDirectPayProgram: true,
      paymentsHistory: [
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_CAPTURED',
          date: '2026-06-20',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.ATTEND',
        },
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_AUTHORIZED',
          date: '2026-06-16',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.COURSES_APPROVED',
        },
      ],
    }),
  },
  {
    key: 'receipt-uploaded',
    label: 'CourseCash used, receipt in → evidence remaining',
    application: buildApplication({
      // Fully spent, so the remaining balance (`reserved`) is 0 — the $1,018.00
      // original allocation is used + reserved. The subtotals are pinned because
      // their defaults key off `reserved > 0`, which no longer holds here.
      reserved: 0,
      used: 101800,
      requestedCourseCashSubtotal: 100000,
      requestedNonCourseCashSubtotal: 39600,
      paidTotal: 101800,
      receiptUploaded: true,
      isEvidenceRequired: true,
      isMissingRequiredEvidence: true,
      shouldDisplayPaymentDetails: true,
      isDirectPayProgram: true,
      paymentsHistory: [
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_CAPTURED',
          date: '2026-06-20',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.ATTEND',
        },
        {
          amount: 101800,
          approverName: 'Karen Advocate',
          assistanceType: 'CARD_AUTHORIZED',
          date: '2026-06-16',
          from: 'TAS.ApplicationState.COURSES_APPROVED',
          logType: 'assistance',
          to: 'TAS.ApplicationState.COURSES_APPROVED',
        },
      ],
    }),
  },
  {
    key: 'pending-capture-swipe',
    // Swiped but not yet captured: the authorization holds funds while `used`
    // stays 0, so the Use CourseCa$h card is still the active task — the swipe
    // must show inside it as a Processing row rather than nowhere.
    label: 'CourseCa$h™ swiped, authorization pending capture (Processing row)',
    application: buildApplication({
      reserved: 76800,
      isDirectPayProgram: true,
      pendingCaptureSwipe: true,
    }),
  },
  {
    key: 'uploaded-receipt-swipe',
    // Receipt already uploaded (status 'uploaded' via a live PENDING
    // approvable): no upload-receipt task anymore, so the swipe renders as a
    // status row whose View button re-opens the receipt modal (#10291).
    label: 'Receipt uploaded → in-review status row with View entry point',
    application: buildApplication({
      reserved: 66800,
      used: 35000,
      isDirectPayProgram: true,
      uploadedReceiptSwipe: true,
    }),
  },
  {
    key: 'disbursement-reimbursement-requested',
    label: 'Disbursement — Reimbursement: requested',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
    }),
  },
  {
    key: 'disbursement-reimbursement-pre-approved',
    label: 'Disbursement — Reimbursement: pre-approved',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_APPROVED',
      isApproved: true,
    }),
  },
  {
    key: 'disbursement-reimbursement-pending',
    label: 'Disbursement — Reimbursement: pending (evidence approved, not yet settled)',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      paidTotal: 0,
    }),
  },
  {
    key: 'disbursement-reimbursement-paid',
    label: 'Disbursement — Reimbursement: paid',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      paidTotal: 139600,
    }),
  },
  {
    key: 'disbursement-course-cash-requested',
    label: 'Disbursement — CourseCa$h™ only: requested (not yet approved, no card issued)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
      requestedCourseCashSubtotal: 100000,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
    }),
  },
  {
    key: 'disbursement-course-cash-available',
    // reserved IS the original allocation here since nothing's been spent yet
    // (reserved == remaining == original total when used is 0).
    label: 'Disbursement — CourseCa$h™ only: available (approved, card reserved, unspent)',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_APPROVED',
      isApproved: true,
      reserved: 101800,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
      courseCashExpiresAt: '2026-09-01',
    }),
  },
  {
    key: 'disbursement-course-cash-active',
    // `reserved` models the CURRENT remaining balance (not a fixed original
    // total — see disbursement.ts's module doc), so a $1,018.00 original
    // allocation with $500.00 spent leaves $518.00 reserved, not $1,018.00.
    label: 'Disbursement — CourseCa$h™ only: active (partially spent, progress bar)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      reserved: 51800,
      used: 50000,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
      courseCashExpiresAt: '2026-09-01',
    }),
  },
  {
    key: 'disbursement-course-cash-deactivated-partial',
    // Deactivated mid-spend: funds remain but the card is dead — the shape the
    // completed drawer's isActive gate exists for (#10309 review): remaining
    // balance alone must not advertise inactive funds as usable.
    label: 'Disbursement — CourseCa$h™ only: deactivated with a remaining balance',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      reserved: 41800,
      used: 60000,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
      pcsCardDeactivated: true,
    }),
  },
  {
    key: 'disbursement-course-cash-deactivated',
    // Fully spent → reserved (remaining) is 0; the $1,018.00 original total is
    // used + reserved = 101800 + 0.
    label: 'Disbursement — CourseCa$h™ only: deactivated',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      reserved: 0,
      used: 101800,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
      pcsCardDeactivated: true,
    }),
  },
  {
    key: 'disbursement-course-cash-deactivated-unspent',
    // Unspent funds on a dead card — the shape #10288's task-status guard
    // exists for: without it the panel rendered a pending "Use CourseCash"
    // invitation for a card that would decline.
    label: 'Disbursement — CourseCa$h™ only: deactivated with unspent funds',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      reserved: 101800,
      used: 0,
      requestedNonCourseCashSubtotal: 0,
      isDirectPayProgram: true,
      pcsCardDeactivated: true,
    }),
  },
  {
    key: 'disbursement-pre-paid-pending',
    label: 'Disbursement — Pre-Paid: pending (approved, payment not yet disbursed)',
    application: buildApplication({
      state: 'TAS.ApplicationState.COURSES_APPROVED',
      isApproved: true,
      isPrepaidProgram: true,
    }),
  },
  {
    key: 'disbursement-pre-paid-paid',
    label: 'Disbursement — Pre-Paid: paid',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      isPrepaidProgram: true,
      paidTotal: 139600,
    }),
  },
  {
    key: 'disbursement-reimbursement-multiple-payments',
    // The 8/4 QA scenario behind #10284: a first settled payment plus a second
    // payment still pending — both must show as their own rows, not fold into
    // one aggregate figure.
    label: 'Disbursement — Reimbursement: paid twice (second payment pending)',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      approvedTotal: 1000,
      requestedTotal: 1000,
      requestedNonCourseCashSubtotal: 1000,
      paidTotal: 500,
      reimbursementAssistances: [
        { amount: 500, state: 'TAS.AssistanceState.SETTLED', settledDate: '2026-07-28' },
        { amount: 500, state: 'TAS.AssistanceState.PAYABLE' },
      ],
    }),
  },
  {
    key: 'disbursement-prepaid-template-reimbursement-money',
    // #10283: the template's ON_ATTEND timing flag says "Pre-Paid", but the
    // application's own money moves as reimbursement — the card must say
    // Reimbursement once such an assistance exists.
    label: 'Disbursement — ON_ATTEND template whose money is reimbursement (labels Reimbursement)',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      isPrepaidProgram: true,
      approvedTotal: 600,
      requestedTotal: 600,
      requestedNonCourseCashSubtotal: 600,
      paidTotal: 600,
      reimbursementAssistances: [
        { amount: 600, state: 'TAS.AssistanceState.SETTLED', settledDate: '2026-08-01' },
      ],
    }),
  },
  {
    key: 'disbursement-mixed-course-cash-and-reimbursement',
    // Same $1,018.00-original / $600.00-spent CC$ story as the active fixture
    // above — reserved (remaining) is the original total minus used.
    label: 'Disbursement — mixed: CourseCa$h™ active + Reimbursement pending, side by side',
    application: buildApplication({
      state: 'TAS.ApplicationState.FULFILLED',
      isApproved: true,
      reserved: 41800,
      used: 60000,
      paidTotal: 0,
      isDirectPayProgram: true,
    }),
  },
  {
    key: 'disbursement-nothing-requested',
    label: 'Disbursement — hidden (nothing requested, no CC$)',
    application: buildApplication({
      state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      shouldDisplayApprovedTotal: false,
      requestedTotal: 0,
      requestedNonCourseCashSubtotal: 0,
    }),
  },
  {
    key: 'disbursement-reimbursement-elected-on-cc-program',
    // The 8/7 QA scenario behind #10392: a CC$-capable program whose
    // application elected reimbursement. The rail used to split the money by
    // the tuition heuristic — Reimbursement requested $4.00 / approved $6.00
    // plus a phantom $2.00 CourseCa$h™ card — for what is a single $6.00
    // reimbursement. One card, requested = approved = $6.00.
    label: 'Disbursement — reimbursement-elected application on a CC$ program (no phantom card)',
    application: buildApplication({
      state: 'TAS.ApplicationState.ATTEND',
      isApproved: true,
      isDirectPayProgram: true,
      courseCashElected: false,
      approvedTotal: 600,
      requestedTotal: 600,
      requestedCourseCashSubtotal: 200,
      requestedNonCourseCashSubtotal: 400,
      paidTotal: 0,
      reimbursementAssistances: [{ amount: 600, state: 'TAS.AssistanceState.PAYABLE' }],
    }),
  },
];
